import { groupBySection, packColumns, type MenuColumn } from '../paginate.js';
import type { MenuBoardContent } from '../types.js';
import type { MenuTemplate } from './common.js';
import {
  WN_FAMILY,
  WN_THEME,
  WN_VARIANTS,
  wnColumns,
  wnHasPhotos,
  wnHeader,
  wnHeaderCss,
  wnHeaderHeight,
  wnPhoto,
  wnPhotoCss,
  wnPhotoIds,
  wnRootCss,
  wnRowsCss,
  wnSection,
} from './walnut-columns.js';

/**
 * Walnut, layout B: a wall of six dish photos down one side of the wood board, and beside it
 * the board's title and the menu in two columns under the bright tab headings. Portrait puts
 * the photos in a band between the title and the lists.
 */

const S = '.mb-t-walnut-wall';
const TITLE_EM = 4.4;
/** Height of the photo band in portrait. */
const PORTRAIT_WALL = 40;
/** Portrait sets the lists larger, so they fill the tall screen. */
const PORTRAIT_SCALE = 1.2;

function columns(board: MenuBoardContent, portrait: boolean): MenuColumn[] {
  const header = wnHeaderHeight(board, TITLE_EM) + 2;
  const wall = portrait && wnHasPhotos(board) ? PORTRAIT_WALL + 2 : 0;
  const wide = !wnHasPhotos(board);
  return portrait
    ? wnColumns(board, 2, (120 - 5 - header - wall) / PORTRAIT_SCALE, 32)
    : wnColumns(board, 2, 67.5 - 5 - header, wide ? 56 : 34);
}

export const walnutWall: MenuTemplate = {
  id: 'walnut-wall',
  name: 'Walnut · Photo wall',
  description:
    'Six dish photos down one side of a dark wood board, and beside them the title and the menu in two columns under bright tab headings. Matches the other Walnut screens.',
  defaultTheme: WN_THEME,
  variants: WN_VARIANTS,
  cuisines: ['burgers', 'cafe', 'drinks'],
  family: { ...WN_FAMILY, layout: 'Photo wall' },
  uses: { featured: false, sectionImages: false, brand: true, decorations: [] },

  paginate(board, orientation) {
    return packColumns(groupBySection(board), columns(board, orientation === 'portrait'), { balance: true });
  },

  render(ctx) {
    const photos = wnHasPhotos(ctx.board);
    const wall = photos
      ? `<div class="wn2-wall">${wnPhotoIds(ctx, 6)
          .map((id) => wnPhoto(ctx, id, 'wn2-p'))
          .join('')}</div>`
      : '';
    const lists = ctx.page.columns.map((c) => `<div class="wn2-col">${c.map((b) => wnSection(ctx, b)).join('')}</div>`).join('');
    return [
      `<div class="wn2-grid${photos ? '' : ' wn2-nowall'}">`,
      wall,
      wnHeader(ctx, 'wn2-header', ctx.orientation === 'portrait' ? 15 : 17),
      `<div class="wn2-cols">${lists}</div>`,
      '</div>',
    ].join('');
  },

  css: `
${wnRootCss(S)}
${S}{padding:2.5em 3em}
${wnRowsCss(S)}
${wnPhotoCss(S)}
${wnHeaderCss(S, 'wn2-header', TITLE_EM)}
${S} .wn2-grid{flex:1;min-height:0;display:grid;grid-template-columns:minmax(0,37fr) minmax(0,63fr);grid-template-rows:auto 1fr;
  grid-template-areas:"wall head" "wall list";column-gap:3.5em}
${S} .wn2-nowall{grid-template-columns:minmax(0,1fr);grid-template-areas:"head" "list"}
${S}.mb-portrait .wn2-grid{grid-template-columns:minmax(0,1fr);grid-template-rows:auto auto 1fr;grid-template-areas:"head" "wall" "list"}
${S}.mb-portrait .wn2-nowall{grid-template-rows:auto 1fr;grid-template-areas:"head" "list"}
${S} .wn2-header{grid-area:head;margin-bottom:2em}
${S} .wn2-header .mb-2nd{text-align:left}
${S} .wn2-nowall .wn2-header,${S}.mb-portrait .wn2-header,${S} .wn2-nowall .wn2-header .mb-2nd,${S}.mb-portrait .wn2-header .mb-2nd{text-align:center}
${S} .wn2-wall{grid-area:wall;min-height:0;display:grid;grid-template-columns:repeat(2,minmax(0,1fr));grid-template-rows:repeat(3,minmax(0,1fr));gap:1.2em}
${S}.mb-portrait .wn2-wall{grid-template-columns:repeat(3,minmax(0,1fr));grid-template-rows:repeat(2,minmax(0,1fr));height:${PORTRAIT_WALL}em;margin-bottom:2em}
${S} .wn2-cols{grid-area:list;min-height:0;display:grid;grid-template-columns:repeat(2,minmax(0,1fr));column-gap:3em}
${S}.mb-portrait .wn2-cols{font-size:${PORTRAIT_SCALE}em;column-gap:2.4em}
${S} .wn2-col{min-width:0;display:flex;flex-direction:column}
`,
};
